import {Button} from '@dropbox/dig-components/buttons';
import {List} from '@dropbox/dig-components/list';
import type {ContentRefObject} from '@dropbox/dig-components/menu';
import {Menu} from '@dropbox/dig-components/menu';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {DeleteLine, RedactLine, TeamLine} from '@dropbox/dig-icons/assets';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {pulseUserAtom} from 'atoms/auth';
import {EmployeeLdap, TeamWithCounts} from 'client';
import {Avatar, AvatarIcon} from 'components/DSYS/Avatar';
import {CancelButton} from 'components/DSYS/CancelButton';
import {$isRedactionNode, $unwrapRedactionNode} from 'components/DSYS/editor/nodes/RedactionNode';
import {GlobalSearchTypeahead} from 'components/DSYS/GlobalSearchTypeahead';
import {useOutsideClick} from 'hooks/useOutsideClick';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {$getNodeByKey, COMMAND_PRIORITY_LOW, createCommand, LexicalEditor} from 'lexical';
import {useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import styles from './FloatingRedactionEditor.module.css';

type RedactionEditorPayload = {
  nodeKey?: string;
  nodeKeys?: string[];
  anchorNodeKey?: string;
};
export const TOGGLE_REDACTION_EDITOR_COMMAND = createCommand<RedactionEditorPayload>();

export const FloatingRedactionEditorPlugin = ({collabOwner}: {collabOwner: EmployeeLdap}) => {
  const [editor] = useLexicalComposerContext();
  const [activeNodeKeys, setActiveNodeKeys] = useState<string[]>([]);
  const [activeAnchorNodeKey, setActiveAnchorNodeKey] = useState<string | null>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  const menuContentRef = useRef<ContentRefObject>(null);

  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeLdap[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<TeamWithCounts[]>([]);

  const closeEditor = useCallback(() => {
    setActiveNodeKeys([]);
    setActiveAnchorNodeKey(null);
    setAnchorElement(null);
    anchorRef.current = null;
  }, []);

  const syncAnchorElement = useCallback(
    (nodeKey: string | null) => {
      if (!nodeKey) {
        anchorRef.current = null;
        setAnchorElement(null);
        return;
      }

      let isValidRedactionNode = false;
      editor.getEditorState().read(() => {
        const node = $getNodeByKey(nodeKey);
        isValidRedactionNode = Boolean(node && $isRedactionNode(node) && node.isCurrentUserOwner());
      });

      if (!isValidRedactionNode) {
        closeEditor();
        return;
      }

      const candidateAnchor = editor.getElementByKey(nodeKey);
      const nextAnchor =
        candidateAnchor instanceof HTMLElement && candidateAnchor.isConnected
          ? candidateAnchor
          : null;
      anchorRef.current = nextAnchor;
      setAnchorElement((current) => (current === nextAnchor ? current : nextAnchor));
    },
    [closeEditor, editor]
  );

  // Show redaction editor when a redaction is triggered/created
  useEffect(() => {
    return editor.registerCommand(
      TOGGLE_REDACTION_EDITOR_COMMAND,
      ({nodeKey, nodeKeys, anchorNodeKey}: RedactionEditorPayload) => {
        const normalizedNodeKeys =
          nodeKeys && nodeKeys.length > 0 ? nodeKeys : nodeKey ? [nodeKey] : [];
        const normalizedAnchorNodeKey = anchorNodeKey ?? normalizedNodeKeys[0] ?? null;

        if (normalizedNodeKeys.length === 0 || !normalizedAnchorNodeKey) {
          return false;
        }

        editor.read(() => {
          const nodes = normalizedNodeKeys
            .map((currentNodeKey) => $getNodeByKey(currentNodeKey))
            .filter(
              (currentNode): currentNode is NonNullable<typeof currentNode> => currentNode !== null
            );

          if (
            nodes.length !== normalizedNodeKeys.length ||
            nodes.some((node) => !$isRedactionNode(node) || !node.isCurrentUserOwner())
          ) {
            return;
          }

          const [firstNode] = nodes;
          if (!firstNode || !$isRedactionNode(firstNode)) {
            return;
          }

          // Update redaction editor with existing users and teams
          const users = firstNode.getUsersSnapshot();
          const teams = firstNode.getTeamsSnapshot();
          setSelectedEmployees(users);
          setSelectedTeams(teams);
          setActiveNodeKeys(normalizedNodeKeys);
          setActiveAnchorNodeKey(normalizedAnchorNodeKey);
        });
        return true;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor]);

  useEffect(() => {
    if (!activeAnchorNodeKey) {
      anchorRef.current = null;
      setAnchorElement(null);
      return;
    }

    let rafId: number | null = null;
    const refreshAnchor = () => {
      if (rafId !== null) {
        return;
      }

      rafId = requestAnimationFrame(() => {
        rafId = null;
        syncAnchorElement(activeAnchorNodeKey);
        menuContentRef.current?.update();
      });
    };

    refreshAnchor();

    const unregisterUpdate = editor.registerUpdateListener(() => {
      refreshAnchor();
    });

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      unregisterUpdate();
    };
  }, [activeAnchorNodeKey, editor, syncAnchorElement]);

  useEffect(() => {
    if (!activeAnchorNodeKey || !anchorElement) {
      return;
    }

    menuContentRef.current?.update();
  }, [activeAnchorNodeKey, anchorElement]);

  if (!activeAnchorNodeKey || activeNodeKeys.length === 0 || !anchorElement) return null;

  return createPortal(
    <FloatingRedactionEditor
      editor={editor}
      nodeKeys={activeNodeKeys}
      anchorRef={anchorRef}
      menuContentRef={menuContentRef}
      collabOwner={collabOwner}
      closeEditor={closeEditor}
      selectedEmployees={selectedEmployees}
      setSelectedEmployees={setSelectedEmployees}
      selectedTeams={selectedTeams}
      setSelectedTeams={setSelectedTeams}
    />,
    document.body
  );
};

const FloatingRedactionEditor = ({
  editor,
  nodeKeys,
  anchorRef,
  menuContentRef,
  collabOwner,
  closeEditor,
  selectedEmployees,
  setSelectedEmployees,
  selectedTeams,
  setSelectedTeams,
}: {
  editor: LexicalEditor;
  nodeKeys: string[];
  anchorRef: React.RefObject<HTMLElement>;
  menuContentRef: React.RefObject<ContentRefObject>;
  collabOwner: EmployeeLdap;
  closeEditor: () => void;
  selectedEmployees: EmployeeLdap[];
  setSelectedEmployees: (employees: EmployeeLdap[]) => void;
  selectedTeams: TeamWithCounts[];
  setSelectedTeams: (teams: TeamWithCounts[]) => void;
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);

  useOutsideClick(editorRef, (e) => {
    // Don't close redaction editor if user clicks on the redacted text
    const redactedEl = (e.target as HTMLElement | null)?.closest<HTMLElement>(
      '[data-redaction-id]'
    );
    if (nodeKeys.includes(redactedEl?.getAttribute('data-redaction-id') ?? '')) return;

    closeEditor();
  });

  const handleRemoveRedactionItem = (id: string) => {
    if (selectedEmployees.find((e) => e.user_id === id)) {
      setSelectedEmployees(selectedEmployees.filter((e) => e.user_id !== id));
    } else {
      setSelectedTeams(selectedTeams.filter((team) => team.team_id !== id));
    }
  };

  const handleSave = () => {
    editor.update(() => {
      nodeKeys.forEach((nodeKey) => {
        const node = $getNodeByKey(nodeKey);
        if (!node || !$isRedactionNode(node) || !node.isCurrentUserOwner()) return;

        node.setUsers(selectedEmployees);
        node.setTeams(selectedTeams);
      });
      closeEditor();
    });
  };

  const handleRemoveRedaction = () => {
    editor.update(() => {
      let selectionTarget: {selectStart: () => void} | null = null;

      for (let index = nodeKeys.length - 1; index >= 0; index -= 1) {
        const nodeKey = nodeKeys[index];
        const node = $getNodeByKey(nodeKey);
        if (!node || !$isRedactionNode(node) || !node.isCurrentUserOwner()) continue;

        const restoredNodes = $unwrapRedactionNode(node);
        if (restoredNodes.length > 0) {
          selectionTarget = restoredNodes[0] as {selectStart: () => void};
        }
      }

      selectionTarget?.selectStart();

      closeEditor();
    });
  };

  const hasChanges = selectedEmployees.length > 0 || selectedTeams.length > 0;
  return (
    <Box ref={editorRef}>
      <Menu.Content ref={menuContentRef} triggerRef={anchorRef} open={true} minWidth="326px">
        <Menu.Segment>
          <Box
            display="flex"
            alignItems="center"
            paddingTop="4"
            paddingRight="12"
            paddingBottom="4"
            paddingLeft="12"
            style={{gap: '4px'}}
          >
            <Box style={{marginLeft: '-3px'}}>
              <UIIcon src={RedactLine} />
            </Box>
            <Text isBold>{t('redact_text')}</Text>
          </Box>
        </Menu.Segment>
        <Menu.Segment style={{paddingBottom: 0}}>
          <Box className={styles.redactionEditorContent}>
            <Box paddingLeft="12" paddingRight="12">
              <GlobalSearchTypeahead
                filter="people,teams"
                textInputSize="small"
                placeholderText={t('add_people_or_teams')}
                selections={[
                  ...selectedTeams.map((team) => team.team_id),
                  ...selectedEmployees.map((e) => e.user_id),
                ]}
                onSelect={(name, type, uid, id) => {
                  if (type === 'employee') {
                    setSelectedEmployees?.([
                      ...selectedEmployees,
                      {
                        user_id: id,
                        ldap: uid,
                        name: name,
                      } as EmployeeLdap,
                    ]);
                  } else {
                    setSelectedTeams?.([
                      ...selectedTeams,
                      {
                        team_id: id,
                        slug: uid,
                        name,
                      } as TeamWithCounts,
                    ]);
                  }
                }}
              />
              <RedactionCountText
                peopleCount={selectedEmployees.length}
                teamsCount={selectedTeams.length}
              />
            </Box>
            <Box className={styles.redactionListScrollContainer}>
              <List spacing="small" isSelectable style={{marginTop: '-6px'}}>
                {selectedTeams.map((team) => (
                  <RedactionListItem
                    key={`redaction-item__${team.slug}`}
                    collabOwner={collabOwner}
                    id={team.slug ?? ''}
                    name={team.name ?? ''}
                    icon={<AvatarIcon src={TeamLine} size="medium" />}
                    onClick={() => handleRemoveRedactionItem(team.team_id)}
                  />
                ))}
                {selectedEmployees.map((employee) => (
                  <RedactionListItem
                    key={`redaction-item__${employee.ldap}`}
                    collabOwner={collabOwner}
                    id={employee.ldap}
                    name={employee.name}
                    icon={<Avatar user={employee} />}
                    onClick={() => handleRemoveRedactionItem(employee.user_id)}
                  />
                ))}
              </List>
            </Box>
          </Box>
        </Menu.Segment>
        <Menu.Segment className={styles.redactionEditorFooter}>
          <Button
            variant="transparent"
            onClick={handleRemoveRedaction}
            className={styles.removeButton}
          >
            {t('remove')}
          </Button>
          <Split gap="8" alignX="right">
            <CancelButton onClick={closeEditor}>{t('cancel')}</CancelButton>
            <Button variant="primary" onClick={handleSave} disabled={!hasChanges}>
              {t('save')}
            </Button>
          </Split>
        </Menu.Segment>
      </Menu.Content>
    </Box>
  );
};

const RedactionCountText = ({
  peopleCount,
  teamsCount,
}: {
  peopleCount: number;
  teamsCount: number;
}) => {
  if (peopleCount === 0 && teamsCount === 0) return null;
  if (peopleCount === 0) {
    return (
      <Box className={styles.redactionCountText}>
        <Text isBold>{t('team', {count: teamsCount})}</Text>
        <Text> {t('may_view_this_redaction')}</Text>
      </Box>
    );
  }
  return (
    <Box className={styles.redactionCountText}>
      <Text isBold>
        {t('people_count', {
          count: peopleCount,
        })}
      </Text>
      {teamsCount > 0 && (
        <>
          <Text> {t('and')} </Text>
          <Text isBold>{t('team', {count: teamsCount})}</Text>
        </>
      )}
      <Text> {t('may_view_this_redaction')}</Text>
    </Box>
  );
};

const RedactionListItem = ({
  collabOwner,
  id,
  name,
  icon,
  onClick,
}: {
  collabOwner: EmployeeLdap;
  id: string;
  name: string;
  icon: React.ReactNode;
  onClick: () => void;
}) => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const canDelete = pulseUser?.ldap !== id && collabOwner.ldap !== id;
  const [isHovered, setIsHovered] = useState(false);
  return (
    <List.Item
      key={`redaction-item__${id}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={canDelete ? onClick : undefined}
    >
      <List.Accessory style={{width: '32px', height: '32px'}}>{icon}</List.Accessory>
      <List.Content>
        <Text>{name}</Text>
      </List.Content>
      {canDelete && (
        <List.Accessory>
          {isHovered && <UIIcon color="var(--dig-color__text__subtle)" src={DeleteLine} />}
        </List.Accessory>
      )}
    </List.Item>
  );
};
